import React, { useReducer, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BanknotesIcon, DocumentTextIcon, ArrowPathIcon, EyeIcon } from '@heroicons/react/24/outline';
import moment from 'moment';

// --- START: MOCK DATA & STATE MANAGEMENT LOGIC ---

// Initial state for the reducer
const initialState = {
    currentSalary: null,
    history: [],
    isLoading: true,
    error: null,
    selectedPayStub: null,
    isModalOpen: false,
};

// Reducer function to handle state transitions
const salaryReducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_SUCCESS':
            return {
                ...state,
                currentSalary: action.payload.currentSalary,
                history: action.payload.history,
                isLoading: false,
            };
        case 'FETCH_ERROR':
            return {
                ...state,
                isLoading: false,
                error: 'Failed to fetch salary data.',
            };
        case 'OPEN_PAY_STUB_MODAL':
            return {
                ...state,
                selectedPayStub: action.payload,
                isModalOpen: true,
            };
        case 'CLOSE_PAY_STUB_MODAL':
            return {
                ...state,
                selectedPayStub: null,
                isModalOpen: false,
            };
        default:
            return state;
    }
};

// Custom Hook to manage salary data and API simulation
const useSalaryData = () => {
    const [state, dispatch] = useReducer(salaryReducer, initialState);

    useEffect(() => {
        // Simulate an API call
        const fetchSalaryData = async () => {
            try {
                await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulate network delay
                const mockData = {
                    currentSalary: { amount: '₹85,000', effectiveDate: 'October 1, 2025' },
                    history: [
                        { id: 1, period: 'Sep 2025', gross: '₹95,000', deductions: '₹10,000', net: '₹85,000' },
                        { id: 2, period: 'Aug 2025', gross: '₹95,000', deductions: '₹10,000', net: '₹85,000' },
                        { id: 3, period: 'Jul 2025', gross: '₹85,000', deductions: '₹8,000', net: '₹77,000' },
                    ],
                };
                dispatch({ type: 'FETCH_SUCCESS', payload: mockData });
            } catch (error) {
                dispatch({ type: 'FETCH_ERROR', error: error.message });
            }
        };

        fetchSalaryData();
    }, []);

    return { state, dispatch };
};

// --- END: MOCK DATA & STATE MANAGEMENT LOGIC ---

const Salary = () => {
    const { state, dispatch } = useSalaryData();
    const { currentSalary, history, isLoading, error, selectedPayStub, isModalOpen } = state;

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
        exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } },
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="flex flex-col items-center">
                    <ArrowPathIcon className="h-12 w-12 text-blue-500 animate-spin" />
                    <p className="mt-4 text-xl font-medium text-gray-700">Loading data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center p-8 bg-white rounded-lg shadow-xl">
                    <h2 className="text-2xl font-bold text-red-600">Error</h2>
                    <p className="mt-2 text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-100 to-purple-200 p-8 text-gray-800">
            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-7xl mx-auto"
            >
                <h1 className="text-4xl font-extrabold text-indigo-800 flex items-center mb-4">
                    <BanknotesIcon className="h-10 w-10 text-indigo-600 mr-4" />
                    Salary & Pay Stubs
                </h1>
                <p className="text-xl font-light text-indigo-600 mb-8">
                    View your current salary and pay stub history.
                </p>

                {/* Current Salary Card */}
                <motion.div
                    className="bg-white p-8 rounded-3xl shadow-lg mb-8"
                    whileHover={{ scale: 1.02, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)" }}
                    transition={{ duration: 0.3 }}
                >
                    <h3 className="text-2xl font-bold mb-4 text-gray-900 flex items-center">
                        <BanknotesIcon className="h-7 w-7 mr-2 text-gray-500" />
                        Current Salary
                    </h3>
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-3xl font-extrabold text-indigo-600">{currentSalary.amount}</p>
                            <p className="text-sm text-gray-500">Effective from: {currentSalary.effectiveDate}</p>
                        </div>
                        <button className="bg-indigo-50 text-indigo-700 py-2 px-4 rounded-full font-semibold transition-colors hover:bg-indigo-100">
                            View Statement
                        </button>
                    </div>
                </motion.div>

                {/* Pay Stub History */}
                <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
                    <div className="p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            <DocumentTextIcon className="h-7 w-7 mr-2 text-gray-500" />
                            Pay Stub History
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Period</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Pay</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Pay</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {history.map((stub) => (
                                        <motion.tr
                                            key={stub.id}
                                            className="hover:bg-gray-50 transition-colors"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: stub.id * 0.1 }}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{stub.period}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{stub.gross}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{stub.net}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button
                                                    onClick={() => dispatch({ type: 'OPEN_PAY_STUB_MODAL', payload: stub })}
                                                    className="text-indigo-600 hover:text-indigo-900 flex items-center"
                                                >
                                                    <EyeIcon className="h-5 w-5 mr-1" /> View Details
                                                </button>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Pay Stub Details Modal */}
            <AnimatePresence>
                {isModalOpen && selectedPayStub && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50"
                    >
                        <motion.div
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="bg-white rounded-3xl shadow-xl w-full max-w-lg p-8 relative"
                        >
                            <button
                                onClick={() => dispatch({ type: 'CLOSE_PAY_STUB_MODAL' })}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <h3 className="text-2xl font-bold mb-6 text-gray-900">Pay Stub for {selectedPayStub.period}</h3>
                            <div className="space-y-4">
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-gray-600 font-medium">Gross Pay:</span>
                                    <span className="font-bold text-green-600">{selectedPayStub.gross}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-gray-600 font-medium">Deductions:</span>
                                    <span className="font-bold text-red-600">-{selectedPayStub.deductions}</span>
                                </div>
                                <div className="flex justify-between pt-2">
                                    <span className="text-gray-900 text-lg font-bold">Net Pay:</span>
                                    <span className="text-lg font-extrabold text-indigo-600">{selectedPayStub.net}</span>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Salary;